import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import SEO from '@/components/SEO';

const GLOSSARY = [
  {
    letter: 'A',
    terms: [
      {
        term: 'AVDP Ounce (Avoirdupois Ounce)',
        body:
          "The standard ounce used in everyday commerce, general trade and for **base metals such as copper**. One AVDP ounce equals exactly 28.349523125 grams — about 9% lighter than a troy ounce. Sixteen AVDP ounces make one avoirdupois pound (453.59 g). If you buy copper by the ounce, it will almost always be measured in AVDP ounces, not troy ounces.",
        testid: 'glossary-avdp',
      },
      {
        term: 'Assay Certificate',
        body:
          "A certificate issued by a refiner or mint that guarantees the purity, weight and authenticity of a bullion bar or coin. Products sold with an assay certificate typically carry a slightly higher premium but are easier to resell.",
      },
      {
        term: 'Ask Price',
        body:
          "The lowest price at which a dealer is currently willing to sell bullion. The difference between the bid and ask price is the 'spread'.",
      },
    ],
  },
  {
    letter: 'B',
    terms: [
      {
        term: 'Bid Price',
        body:
          "The highest price a dealer is currently willing to pay to buy bullion from you. Always lower than the ask price — the gap between them is the dealer's spread.",
      },
      {
        term: 'Bar',
        body:
          "A rectangular piece of refined precious metal. Bars are typically produced either 'cast' (poured into a mould) or 'minted' (pressed from a blank), and range from 1 gram to 1 kilogram or larger.",
      },
      {
        term: 'Bullion',
        body:
          "Precious metal in its bulk form — bars, coins or rounds — valued by weight and purity rather than by design or rarity. Contrast with 'numismatic' coins which are valued for their collectability.",
        testid: 'glossary-bullion',
      },
      {
        term: 'Buyback',
        body:
          "The service a dealer offers to buy bullion back from customers. Reputable dealers will always offer a competitive buyback price. See our Sell page for our current buyback quotes.",
      },
    ],
  },
  {
    letter: 'C',
    terms: [
      {
        term: 'Cast Bar',
        body:
          "A bar produced by pouring molten metal into a mould. Cast bars typically have a rougher finish than minted bars and slightly lower premiums, making them popular for larger investment stacks.",
      },
      {
        term: 'Coin',
        body:
          "A bullion piece issued by a government mint with a face value (legal tender), even though the metal value is usually far higher than the face value. Popular examples: Gold Maple Leaf, Silver Kangaroo, Britannia.",
      },
      {
        term: 'Copper',
        body:
          "A base metal traded internationally. At Southern Bullion Exchange we sell copper bullion at wholesale-adjacent premiums. **Note: copper is measured in AVDP ounces** (28.35 g) or by weight in kg, not in troy ounces.",
        testid: 'glossary-copper',
      },
    ],
  },
  {
    letter: 'F',
    terms: [
      {
        term: 'Fineness / Purity',
        body:
          "The proportion of pure precious metal in a bar or coin, expressed as parts per thousand. '.9999 fine' means 99.99% pure gold. '999 fine' means 99.9% pure silver. Higher purity commands a modest premium.",
      },
    ],
  },
  {
    letter: 'L',
    terms: [
      {
        term: 'LBMA (London Bullion Market Association)',
        body:
          "The international trade association for the wholesale over-the-counter precious metals market. LBMA-accredited refiners produce 'Good Delivery' bars that are accepted globally without further assay.",
      },
    ],
  },
  {
    letter: 'M',
    terms: [
      {
        term: 'Mint',
        body:
          "A facility that produces coins or bars. Well-known bullion mints include Perth Mint (Australia), Royal Canadian Mint (Canada), Royal Mint (UK), and PAMP Suisse (Switzerland).",
      },
      {
        term: 'Minted Bar',
        body:
          "A bar pressed from a blank (as opposed to cast). Minted bars have a smoother finish, sharper edges and are often sold in protective packaging with an assay certificate.",
      },
    ],
  },
  {
    letter: 'N',
    terms: [
      {
        term: 'Numismatic',
        body:
          "Refers to coins valued for their rarity, condition, or historical significance rather than just their metal content. Numismatic coins can carry very large premiums over spot. We focus on bullion, not numismatics.",
      },
    ],
  },
  {
    letter: 'P',
    terms: [
      {
        term: 'Premium',
        body:
          "The amount above the spot price that you pay when buying bullion. Premiums cover the cost of minting, distribution, insurance and the dealer's margin. Larger bars typically have lower percentage premiums than small coins.",
        testid: 'glossary-premium',
      },
    ],
  },
  {
    letter: 'R',
    terms: [
      {
        term: 'Round',
        body:
          "A coin-shaped piece of bullion produced by a private mint. Rounds are not legal tender (no face value) but are typically cheaper than government-issued coins of the same weight and purity.",
      },
    ],
  },
  {
    letter: 'S',
    terms: [
      {
        term: 'Spot Price',
        body:
          "The current global market price for one troy ounce of a precious metal, quoted in real time on international exchanges (COMEX, LBMA). Retail prices always sit slightly above spot due to the premium.",
        testid: 'glossary-spot',
      },
      {
        term: 'Spread',
        body:
          "The difference between the bid (buyback) price and the ask (retail) price. A tight spread indicates a competitive and liquid market. Reputable dealers publish both prices transparently.",
      },
    ],
  },
  {
    letter: 'T',
    terms: [
      {
        term: 'Troy Ounce',
        body:
          "The standard unit of weight for precious metals worldwide. One troy ounce equals exactly 31.1034768 grams — about 10% heavier than the ordinary AVDP ounce used for everyday goods. Gold, silver, platinum and palladium are always quoted per troy ounce on international markets. Twelve troy ounces make one troy pound (373.24 g).",
        testid: 'glossary-troy',
      },
    ],
  },
];

// Simple bold-marker parser: **text** → <strong>text</strong>
function renderBody(body) {
  const parts = body.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`b-${i}`} className="text-[#0A0A0A]">{part.slice(2, -2)}</strong>;
    }
    return <span key={`t-${i}`}>{part}</span>;
  });
}

export default function GlossaryPage() {
  return (
    <div data-testid="glossary-page">
      <SEO
        title="Bullion Glossary — Terms Every Investor Should Know"
        description="Plain-English glossary of precious metals and bullion terms including troy ounce vs AVDP ounce, spot price, premium, bid/ask, purity/fineness and more. Aimed at New Zealand investors."
        path="/education/glossary"
      />

      <section className="bg-[#0A0A0A] py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/education" className="inline-flex items-center gap-2 text-[#D4AF37] text-sm mb-6 hover:text-white transition-colors" data-testid="glossary-back-link">
            <ArrowLeft size={16} /> Back to Education Hub
          </Link>
          <p className="section-label mb-3">Reference</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
            Bullion Glossary
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Plain-English definitions of the terms you'll encounter when buying, selling or storing precious metals. Not sure what a term means?
            Look it up here first before you buy.
          </p>
        </div>
      </section>

      {/* Featured callout: Troy vs AVDP */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pt-12">
        <div className="bg-gradient-to-br from-[#faf7ed] to-white border-l-4 border-[#D4AF37] p-6 rounded-r-sm mb-12" data-testid="troy-vs-avdp-callout">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={18} className="text-[#D4AF37]" />
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Troy ounce vs AVDP ounce — quick comparison</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <div className="bg-white p-4 rounded-sm border border-gray-100">
              <p className="font-semibold text-[#D4AF37] mb-1">Troy ounce (t oz)</p>
              <p className="text-xs text-gray-500 mb-2">Used for precious metals worldwide</p>
              <p><strong>= 31.1034768 g</strong></p>
              <p className="text-xs text-gray-500 mt-2">Gold, silver, platinum, palladium.</p>
            </div>
            <div className="bg-white p-4 rounded-sm border border-gray-100">
              <p className="font-semibold text-orange-700 mb-1">AVDP ounce (avoirdupois)</p>
              <p className="text-xs text-gray-500 mb-2">Standard commercial ounce</p>
              <p><strong>= 28.349523125 g</strong></p>
              <p className="text-xs text-gray-500 mt-2">Copper &amp; base metals, everyday commerce.</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            1 troy oz is <strong>~9.7% heavier</strong> than 1 AVDP oz. Always check which unit is quoted — especially when comparing copper prices to precious-metal prices.
          </p>
        </div>
      </section>

      {/* A–Z glossary */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        {GLOSSARY.map(section => (
          <div key={section.letter} className="mb-12" data-testid={`glossary-section-${section.letter}`}>
            <div className="flex items-center gap-3 mb-4 sticky top-0 bg-white/95 backdrop-blur py-2 z-10">
              <div className="w-10 h-10 rounded-full bg-[#0A0A0A] text-[#D4AF37] flex items-center justify-center font-bold text-lg" style={{ fontFamily: 'Cinzel, serif' }}>
                {section.letter}
              </div>
              <div className="h-px bg-gray-200 flex-1" />
            </div>
            <dl className="space-y-6">
              {section.terms.map(({ term, body, testid }) => (
                <div key={term} className="pb-4 border-b border-gray-100 last:border-b-0" data-testid={testid || `glossary-term-${term.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <dt className="font-semibold text-[#0A0A0A] mb-1" style={{ fontFamily: "'Poppins', sans-serif" }}>{term}</dt>
                  <dd className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {renderBody(body)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        ))}

        <div className="mt-16 text-center">
          <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>Term missing? Reach out and we'll add it.</p>
          <Link to="/contact" className="btn-gold text-sm inline-block" data-testid="glossary-contact-link">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}
